import { log } from '@/log'
import Cookies from 'js-cookie'

export class SessionCookie {
  /**
   * JWTのpayload部分をパースして返す
   * API呼び出しによってserver側で必ず検証が行われるため、client側での署名の検証は行わない
   */
  readPayload(): SessionCookiePayload | null {
    const sessionCookie = Cookies.get('mc_s')
    if (!sessionCookie) {
      return null
    }

    const payload = sessionCookie.split('.')[1]
    if (!payload) {
      return null
    }

    try {
      const decodedPayload = JSON.parse(atob(payload))

      if (
        typeof decodedPayload.id !== 'string'
        || typeof decodedPayload.v !== 'number'
        || typeof decodedPayload.startAt !== 'number'
        || typeof decodedPayload.data !== 'object'
      ) {
        log.error('Invalid session cookie payload', decodedPayload)

        return null
      }

      return {
        id: decodedPayload.id,
        v: decodedPayload.v,
        startAt: decodedPayload.startAt,
        data: parseSessionData(decodedPayload.data),
      }
    } catch (error) {
      log.error(error)

      return null
    }
  }

  readPayloadOrFail(): SessionCookiePayload {
    const payload = this.readPayload()
    if (!payload) {
      throw new Error('Session cookie payload not found or invalid')
    }

    return payload
  }

  /**
   * セッションクッキーを削除する
   */
  clear(): void {
    Cookies.remove('mc_s')
  }
}

export interface SessionCookiePayload {
  id: string
  v: number
  startAt: number
  data: SessionData
}

export interface DataFulfilledSessionCookiePayload extends SessionCookiePayload {
  data: FulfilledSessionData
}

export interface DataEmptySessionCookiePayload extends SessionCookiePayload {
  data: EmptySessionData
}

export type SessionData = FulfilledSessionData | EmptySessionData

export interface FulfilledSessionData {
  _marker: 'fulfilled'
  userId: string,
  userShortId: string,
  isRegistered: boolean
}

export interface EmptySessionData {
  _marker: 'empty'
}

export const isDataFulfilledSessionCookiePayload =
  (payload: SessionCookiePayload): payload is DataFulfilledSessionCookiePayload =>
    payload.data._marker === 'fulfilled'

function parseSessionData(data: any): FulfilledSessionData | EmptySessionData {
  const { userId, userShortId, isRegistered } = data
  if (userId === undefined && userShortId === undefined && isRegistered === undefined) {
    return { _marker: 'empty' }
  }

  if (typeof userId !== 'string' || typeof userShortId !== 'string' || typeof isRegistered !== 'boolean') {
    log.error('Invalid session data format, fallback to empty data', data)

    return { _marker: 'empty' }
  }

  if (userId.length === 0 || userShortId.length === 0) {
    log.error('Invalid session data format, fallback to empty data', data)

    return { _marker: 'empty' }
  }

  return { _marker: 'fulfilled', userId, userShortId, isRegistered }
}
